import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Car, Route, CalendarClock, FileBarChart2, FileText, LogOut, UserCog, ClipboardList, Truck, ClipboardCheck, Fuel } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://customer-assets-eiarnc6j.emergentagent.net/job_carpool-manager-6/artifacts/qyxqg7dd_logo%20Padr%C3%A3o.png";

const NITA_EMAIL = "nyttapatrocinio06@gmail.com";

const baseNav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/motoristas", label: "Motoristas", icon: Users, testid: "nav-drivers" },
    { to: "/veiculos", label: "Veículos", icon: Car, testid: "nav-vehicles" },
    { to: "/viagens-fixas", label: "Contratos de Veículos", icon: Route, testid: "nav-trips-fixas" },
    { to: "/viagens-diarias", label: "Viagens Diárias", icon: CalendarClock, testid: "nav-trips-diarias" },
    { to: "/solicitacoes", label: "Solicitações", icon: ClipboardList, testid: "nav-requests" },
    { to: "/fretes", label: "Fretes", icon: Truck, testid: "nav-freights" },
    { to: "/inspecoes", label: "Checklist", icon: ClipboardCheck, testid: "nav-inspections" },
    { to: "/relatorios", label: "Relatórios", icon: FileBarChart2, testid: "nav-reports" },
    { to: "/relatorio-mensal", label: "Relatório do Conselho", icon: FileText, testid: "nav-relatorio-mensal" },
];

// Itens visíveis somente para Admin ou para a usuária Nita
const nitaNav = [
    { to: "/ordens-servico", label: "Ordens de Serviço", icon: ClipboardCheck, testid: "nav-os" },
    { to: "/combustivel", label: "Combustível", icon: Fuel, testid: "nav-fuel" },
];

const adminNav = { to: "/usuarios", label: "Usuários", icon: UserCog, testid: "nav-users" };

export default function Layout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const isAdmin = user?.role === "admin";
    const isNita = user?.email === NITA_EMAIL;
    const restricted = isAdmin || isNita ? nitaNav : [];
    // Ordem final da sidebar: base + itens restritos (OS/Combustível) + Usuários (admin)
    // Inserimos itens restritos após "Solicitações" para manter contexto operacional
    const solicIdx = baseNav.findIndex((n) => n.to === "/solicitacoes");
    const withRestricted = solicIdx >= 0
        ? [...baseNav.slice(0, solicIdx + 1), ...restricted, ...baseNav.slice(solicIdx + 1)]
        : [...baseNav, ...restricted];
    const navItems = isAdmin ? [...withRestricted, adminNav] : withRestricted;
    const current = navItems.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)));

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen flex bg-background" data-testid="app-layout">
            {/* Sidebar */}
            <aside className="w-72 shrink-0 bg-white border-r border-border flex flex-col sticky top-0 h-screen">
                <div className="p-6 border-b border-border flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl bg-emerald-900 flex items-center justify-center p-1 shadow-md border-2 border-emerald-800">
                        <img src={LOGO_URL} alt="COOMFLONA" className="w-full h-full object-contain" />
                    </div>
                    <div>
                        <div className="font-extrabold text-[15px] text-primary leading-tight">COOMFLONA</div>
                        <div className="text-xs text-muted-foreground leading-tight">Setor de Transporte</div>
                    </div>
                </div>

                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150 ${
                                        isActive
                                            ? "bg-primary text-primary-foreground shadow-sm"
                                            : "text-foreground hover:bg-secondary"
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" />
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="p-3 border-t border-border">
                    {user && (
                        <div className="px-2 pb-3">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">Logado como</div>
                            <div className="font-semibold text-sm text-foreground truncate" data-testid="current-user-name">{user.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                        </div>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2"
                        onClick={handleLogout}
                        data-testid="btn-logout"
                    >
                        <LogOut className="w-4 h-4" /> Sair
                    </Button>
                    <div className="pt-3 text-[11px] text-muted-foreground text-center border-t border-border">
                        <div className="mt-2">CNPJ: 07.315.165/0001-00</div>
                        <div className="mt-2 pt-2 border-t border-border/60">
                            <div className="text-[10px] text-muted-foreground/80">Desenvolvido por</div>
                            <div className="font-semibold text-primary text-xs">Railan Farias</div>
                            <div className="text-[10px] text-muted-foreground/80">Cooperado</div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 flex flex-col min-w-0">
                <header className="h-16 bg-white border-b border-border px-8 flex items-center justify-between sticky top-0 z-20">
                    <div>
                        <h1 className="text-xl font-bold text-foreground" data-testid="page-title">
                            {current?.label || "COOMFLONA"}
                        </h1>
                        <div className="text-xs text-muted-foreground">
                            Sistema de controle de viagens
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 pulse-green" />
                        <span className="text-xs font-medium text-muted-foreground">Sistema online</span>
                    </div>
                </header>

                <div className="flex-1 p-8">
                    <Outlet />
                </div>
            </main>

            <Toaster position="top-right" richColors />
        </div>
    );
}
